import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  ChevronDown,
  Loader2,
  MessageSquare,
  Star,
  Trash2,
  WifiOff,
} from "lucide-react";
import { api, type DocProposal } from "@/lib/api";
import {
  isConnectivityError,
  isDocsContentDirty,
  probeServerHealth,
  tagsFromText,
} from "@/lib/docsEditorGuards";
import DocMarkdownEditor from "@/components/DocMarkdownEditor";
import DocChatPanel from "@/components/DocChatPanel";
import DocProposalPreview from "@/components/DocProposalPreview";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

type LeaveGate =
  | { phase: "saving"; target: string | null }
  | { phase: "blocked"; target: string | null; reason: string }
  | null;

export default function DokumentDetail() {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [title, setTitle] = useState("");
  const [tagsText, setTagsText] = useState("");
  const [bodyMd, setBodyMd] = useState("");
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const [metaOpen, setMetaOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(true);
  const [proposal, setProposal] = useState<DocProposal | null>(null);
  const [browserOnline, setBrowserOnline] = useState(
    () => (typeof navigator === "undefined" ? true : navigator.onLine !== false),
  );
  const [apiReachable, setApiReachable] = useState(true);
  const serverReachable = browserOnline && apiReachable;
  const [leaveGate, setLeaveGate] = useState<LeaveGate>(null);
  const [pendingDebounce, setPendingDebounce] = useState(false);

  const titleRef = useRef(title);
  const tagsTextRef = useRef(tagsText);
  const bodyMdRef = useRef(bodyMd);
  const serverReachableRef = useRef(serverReachable);
  const leaveTargetRef = useRef<string | null>(null);
  const bypassLeaveGuardRef = useRef(false);
  const debounceTimerRef = useRef<number | null>(null);
  const skipBodyHydrateRef = useRef(false);

  titleRef.current = title;
  tagsTextRef.current = tagsText;
  bodyMdRef.current = bodyMd;
  serverReachableRef.current = serverReachable;

  const proposalActive = Boolean(proposal?.hunks?.length);

  const docQ = useQuery({
    queryKey: ["docs-doc", id],
    queryFn: () => api.docsGet(id),
    enabled: Boolean(id),
  });
  const foldersQ = useQuery({
    queryKey: ["docs-folders"],
    queryFn: () => api.docsFolders(),
  });

  useEffect(() => {
    const doc = docQ.data?.document;
    if (!doc) return;
    setTitle(doc.title);
    setTagsText((doc.tags || []).join(", "));
    if (!skipBodyHydrateRef.current) {
      setBodyMd(doc.body_md || "");
    }
    skipBodyHydrateRef.current = false;
    setProposal(doc.proposal && doc.proposal.hunks?.length ? doc.proposal : null);
    setHydrated(true);
  }, [docQ.data?.document]);

  const markServerDown = useCallback((err?: unknown) => {
    if (err && !isConnectivityError(err)) return;
    setApiReachable(false);
  }, []);

  const patchMut = useMutation({
    mutationFn: (body: Parameters<typeof api.docsPatch>[1]) => api.docsPatch(id, body),
    onMutate: () => {
      setSaveState("saving");
      setSaveErr(null);
    },
    onSuccess: (res) => {
      setSaveState("saved");
      setApiReachable(true);
      skipBodyHydrateRef.current = true;
      qc.setQueryData(["docs-doc", id], res);
      void qc.invalidateQueries({ queryKey: ["docs-list"] });
      void qc.invalidateQueries({ queryKey: ["docs-tags"] });
    },
    onError: (err: Error) => {
      setSaveState("error");
      setSaveErr(err.message);
      markServerDown(err);
    },
  });

  const deleteMut = useMutation({
    mutationFn: () => api.docsDelete(id),
    onSuccess: () => {
      bypassLeaveGuardRef.current = true;
      void qc.invalidateQueries({ queryKey: ["docs-list"] });
      nav("/dokumenty");
    },
  });

  const proposalMut = useMutation({
    mutationFn: async (args: {
      action: "accept" | "reject";
      hunk_ids?: string[];
    }) => {
      if (args.action === "accept") {
        return api.docsProposalAccept(id, args.hunk_ids ? { hunk_ids: args.hunk_ids } : {});
      }
      return api.docsProposalReject(id, args.hunk_ids ? { hunk_ids: args.hunk_ids } : {});
    },
    onSuccess: (res) => {
      setApiReachable(true);
      const nextBody = res.document?.body_md ?? "";
      setBodyMd(nextBody);
      bodyMdRef.current = nextBody;
      const nextProp =
        res.proposal && res.proposal.hunks?.length
          ? res.proposal
          : res.document?.proposal && res.document.proposal.hunks?.length
            ? res.document.proposal
            : null;
      setProposal(nextProp);
      skipBodyHydrateRef.current = true;
      qc.setQueryData(["docs-doc", id], { document: res.document });
      void qc.invalidateQueries({ queryKey: ["docs-list"] });
    },
    onError: (err: Error) => {
      markServerDown(err);
      window.alert(err.message || "Nie udało się przetworzyć propozycji");
    },
  });

  const serverDoc = docQ.data?.document;
  const contentDirty = isDocsContentDirty({
    hydrated,
    bodyMd,
    title,
    tagsText,
    serverBody: serverDoc?.body_md,
    serverTitle: serverDoc?.title,
    serverTags: serverDoc?.tags,
  });
  const saving = saveState === "saving" || patchMut.isPending;
  const needsLeaveGuard = contentDirty || saving || pendingDebounce;

  const buildPatchPayload = useCallback((): Parameters<typeof api.docsPatch>[1] => {
    const doc = docQ.data?.document;
    const payload: Parameters<typeof api.docsPatch>[1] = {};
    const nextTitle = titleRef.current.trim() || "Bez tytułu";
    const nextTags = tagsFromText(tagsTextRef.current);
    const nextBody = bodyMdRef.current;
    if (!doc || nextBody !== (doc.body_md || "")) payload.body_md = nextBody;
    if (!doc || nextTitle !== doc.title) payload.title = nextTitle;
    const serverTags = (doc?.tags || []).join("\0");
    const localTags = nextTags.join("\0");
    if (!doc || serverTags !== localTags) payload.tags = nextTags;
    if (Object.keys(payload).length === 0) {
      payload.body_md = nextBody;
    }
    return payload;
  }, [docQ.data?.document]);

  const flushSave = useCallback(async () => {
    if (!id || !hydrated) return;
    if (!serverReachableRef.current) {
      throw new Error("Serwer nie odpowiada. Zmiany nie mogą być zapisane.");
    }
    if (debounceTimerRef.current) {
      window.clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
      setPendingDebounce(false);
    }
    await patchMut.mutateAsync(buildPatchPayload());
  }, [buildPatchPayload, hydrated, id, patchMut]);

  useEffect(() => {
    if (!hydrated || !id || proposalActive) {
      setPendingDebounce(false);
      return;
    }
    if (!serverReachable) {
      setPendingDebounce(false);
      if (debounceTimerRef.current) {
        window.clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      return;
    }
    const current = docQ.data?.document?.body_md || "";
    if (bodyMd === current) {
      setPendingDebounce(false);
      return;
    }
    setPendingDebounce(true);
    if (debounceTimerRef.current) window.clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = window.setTimeout(() => {
      debounceTimerRef.current = null;
      setPendingDebounce(false);
      patchMut.mutate({ body_md: bodyMd });
    }, 800);
    return () => {
      if (debounceTimerRef.current) {
        window.clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bodyMd, hydrated, id, serverReachable, proposalActive]);

  useEffect(() => {
    const onOffline = () => setBrowserOnline(false);
    const onOnline = () => {
      setBrowserOnline(true);
      void probeServerHealth().then((ok) => setApiReachable(ok));
    };
    window.addEventListener("offline", onOffline);
    window.addEventListener("online", onOnline);
    return () => {
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("online", onOnline);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        if (!cancelled) setBrowserOnline(false);
        return;
      }
      const ok = await probeServerHealth();
      if (!cancelled) setApiReachable(ok);
    };
    void tick();
    const ms = apiReachable && browserOnline ? 12_000 : 3_000;
    const idTimer = window.setInterval(() => void tick(), ms);
    return () => {
      cancelled = true;
      window.clearInterval(idTimer);
    };
  }, [apiReachable, browserOnline]);

  const wasReachableRef = useRef(true);
  useEffect(() => {
    const was = wasReachableRef.current;
    wasReachableRef.current = serverReachable;
    if (!was && serverReachable && contentDirty && hydrated && !proposalActive) {
      void flushSave().catch(() => {
        /* onError już ustawia banner */
      });
    }
  }, [serverReachable, contentDirty, hydrated, flushSave, proposalActive]);

  const proceedLeave = useCallback(
    (target: string | null) => {
      bypassLeaveGuardRef.current = true;
      setLeaveGate(null);
      if (target) nav(target);
      else nav(-1);
    },
    [nav],
  );

  const attemptLeave = useCallback(
    async (target: string | null) => {
      leaveTargetRef.current = target;
      if (!needsLeaveGuard) {
        proceedLeave(target);
        return;
      }
      if (!serverReachableRef.current) {
        setLeaveGate({
          phase: "blocked",
          target,
          reason:
            "Serwer nie odpowiada. Niezapisane zmiany zostaną utracone, jeśli wyjdziesz teraz.",
        });
        return;
      }
      setLeaveGate({ phase: "saving", target });
      try {
        await flushSave();
        proceedLeave(target);
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : "Nie udało się zapisać przed wyjściem.";
        setLeaveGate({ phase: "blocked", target, reason: msg });
      }
    },
    [flushSave, needsLeaveGuard, proceedLeave],
  );

  useEffect(() => {
    if (!needsLeaveGuard) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [needsLeaveGuard]);

  useEffect(() => {
    if (!needsLeaveGuard) return;
    const onClick = (e: MouseEvent) => {
      if (bypassLeaveGuardRef.current) return;
      if (e.defaultPrevented || e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const el = e.target as Element | null;
      const a = el?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!a || a.target === "_blank" || a.hasAttribute("download")) return;
      const href = a.getAttribute("href");
      if (!href || href.startsWith("#") || href.startsWith("mailto:") || href.startsWith("tel:")) {
        return;
      }
      let url: URL;
      try {
        url = new URL(href, window.location.origin);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin) return;
      const next = `${url.pathname}${url.search}${url.hash}`;
      const cur = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      if (next === cur) return;
      e.preventDefault();
      e.stopPropagation();
      void attemptLeave(next);
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [attemptLeave, needsLeaveGuard]);

  const saveMeta = useCallback(() => {
    if (!serverReachableRef.current) return;
    const tags = tagsFromText(tagsText);
    patchMut.mutate({ title: title.trim() || "Bez tytułu", tags });
  }, [patchMut, tagsText, title]);

  const doc = docQ.data?.document;
  const folders = foldersQ.data?.folders || [];
  const folderName = folders.find((f) => f.id === doc?.folder_id)?.name || "Folder";
  const editsLocked = !serverReachable;
  const bodyEditable = !editsLocked && !proposalActive;

  const saveSubtitle = (() => {
    if (!serverReachable) return "Serwer nie odpowiada. Edycja zablokowana.";
    if (proposalActive) return "Podgląd propozycji AI: edycja treści wstrzymana";
    if (leaveGate?.phase === "saving" || saveState === "saving") return "Zapisywanie…";
    if (pendingDebounce) return "Zmiany czekają na autozapis…";
    if (contentDirty) return "Niezapisane zmiany";
    if (saveState === "error") return saveErr || "Błąd zapisu";
    if (saveState === "saved") return "Zapisano";
    return "Autozapis Markdown";
  })();

  if (docQ.isLoading || !hydrated) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (docQ.isError || !doc) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-red-600">Nie znaleziono dokumentu.</p>
        <Button asChild variant="outline">
          <Link to="/dokumenty">Wróć</Link>
        </Button>
      </div>
    );
  }

  const renderFolderSelect = () => (
    <select
      className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm disabled:opacity-60"
      value={doc.folder_id}
      disabled={editsLocked || saving}
      onChange={(e) => patchMut.mutate({ folder_id: e.target.value })}
    >
      {folders.map((f) => (
        <option key={f.id} value={f.id}>
          {f.name}
        </option>
      ))}
    </select>
  );

  const renderTagsField = () => (
    <Input
      value={tagsText}
      onChange={(e) => setTagsText(e.target.value)}
      onBlur={saveMeta}
      placeholder="mofu, ads"
      disabled={editsLocked}
    />
  );

  const renderTagBadges = () =>
    doc.tags?.length > 0 ? (
      <div className="flex flex-wrap gap-1">
        {doc.tags.map((t) => (
          <Badge key={t} variant="secondary">
            {t}
          </Badge>
        ))}
      </div>
    ) : null;

  return (
    <div className="flex min-h-[calc(100vh-5.5rem)] flex-col space-y-4">
      {!serverReachable ? (
        <div
          role="alert"
          className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950"
        >
          <WifiOff className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
          <div className="min-w-0 space-y-1">
            <p className="font-medium">Serwer nie odpowiada</p>
            <p className="text-amber-900/90">
              Edycja jest zablokowana, bo zmiany nie zostałyby zapisane. Sprawdzam połączenie…
              {contentDirty
                ? " Masz lokalne, jeszcze niezapisane zmiany. Po odzyskaniu połączenia zapiszę je automatycznie."
                : null}
            </p>
          </div>
          <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-amber-700" />
        </div>
      ) : null}

      <PageHeader
        title="Edycja dokumentu"
        subtitle={saveSubtitle}
        actions={
          <>
            <Button
              variant="outline"
              type="button"
              onClick={() => void attemptLeave("/dokumenty")}
            >
              <ArrowLeft className="mr-1.5 h-4 w-4" />
              Lista
            </Button>
            <Button
              variant={chatOpen ? "brand" : "outline"}
              type="button"
              onClick={() => setChatOpen((v) => !v)}
            >
              <MessageSquare className="mr-1.5 h-4 w-4" />
              <span className="hidden sm:inline">Chat</span>
            </Button>
            <Button
              variant={doc.starred ? "brand" : "outline"}
              disabled={editsLocked || saving}
              onClick={() => patchMut.mutate({ starred: !doc.starred })}
            >
              <Star className={cn("mr-1.5 h-4 w-4", doc.starred ? "fill-current" : "")} />
              <span className="hidden sm:inline">Gwiazdka</span>
            </Button>
            <Button
              variant="ghost"
              className="text-red-600"
              disabled={editsLocked || saving}
              onClick={() => {
                if (window.confirm(`Usunąć „${doc.title}”?`)) deleteMut.mutate();
              }}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </>
        }
      />

      <div className="rounded-xl border border-slate-200 bg-white sm:hidden">
        <div className="flex items-center gap-1.5 px-2 py-1.5">
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={saveMeta}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.currentTarget.blur();
            }}
            disabled={editsLocked}
            className="h-9 flex-1 border-0 bg-transparent shadow-none focus-visible:ring-0"
            placeholder="Tytuł"
            aria-label="Tytuł"
          />
          <button
            type="button"
            onClick={() => setMetaOpen((v) => !v)}
            className="inline-flex h-9 shrink-0 items-center gap-1 rounded-md px-2 text-xs text-slate-500 hover:bg-slate-50"
            aria-expanded={metaOpen}
            aria-label={metaOpen ? "Zwiń szczegóły" : "Rozwiń szczegóły"}
          >
            <span className="max-w-[5.5rem] truncate">{folderName}</span>
            <ChevronDown
              className={cn("h-4 w-4 transition-transform", metaOpen && "rotate-180")}
            />
          </button>
        </div>
        {metaOpen ? (
          <div className="space-y-3 border-t border-slate-100 p-3">
            <label className="block space-y-1">
              <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Folder
              </span>
              {renderFolderSelect()}
            </label>
            <label className="block space-y-1">
              <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Tagi (przecinek)
              </span>
              {renderTagsField()}
            </label>
            {renderTagBadges()}
          </div>
        ) : null}
      </div>

      <div className="hidden gap-3 rounded-xl border border-slate-200 bg-white p-4 sm:grid sm:grid-cols-2">
        <label className="block space-y-1 sm:col-span-2">
          <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Tytuł</span>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={saveMeta}
            disabled={editsLocked}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.currentTarget.blur();
            }}
          />
        </label>
        <label className="block space-y-1">
          <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Folder</span>
          {renderFolderSelect()}
        </label>
        <label className="block space-y-1">
          <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
            Tagi (przecinek)
          </span>
          {renderTagsField()}
        </label>
        {(() => {
          const badges = renderTagBadges();
          return badges ? <div className="sm:col-span-2">{badges}</div> : null;
        })()}
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-3 lg:flex-row">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          {proposalActive && proposal ? (
            <DocProposalPreview
              proposal={proposal}
              disabled={editsLocked}
              pending={proposalMut.isPending}
              onAcceptAll={() => proposalMut.mutate({ action: "accept" })}
              onRejectAll={() => proposalMut.mutate({ action: "reject" })}
              onAcceptSelected={(ids) =>
                proposalMut.mutate({ action: "accept", hunk_ids: ids })
              }
              onRejectSelected={(ids) =>
                proposalMut.mutate({ action: "reject", hunk_ids: ids })
              }
            />
          ) : (
            <DocMarkdownEditor
              documentId={id}
              initialMarkdown={bodyMd}
              onMarkdownChange={setBodyMd}
              editable={bodyEditable}
              minHeightClass="min-h-[calc(100vh-22rem)]"
            />
          )}
        </div>

        {chatOpen ? (
          <div className="flex h-[min(52vh,420px)] w-full shrink-0 flex-col lg:h-auto lg:w-[340px]">
            <DocChatPanel
              documentId={id}
              proposalActive={proposalActive}
              disabled={editsLocked}
              flushBeforeSend={async () => {
                if (proposalActive) return;
                if (contentDirty || pendingDebounce) await flushSave();
              }}
              onProposalChange={(p) => {
                setProposal(p && p.hunks?.length ? p : null);
              }}
            />
          </div>
        ) : null}
      </div>

      {leaveGate ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="docs-leave-title"
            className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-5 shadow-lg"
          >
            {leaveGate.phase === "saving" ? (
              <div className="flex flex-col items-center gap-3 py-2 text-center">
                <Loader2 className="h-8 w-8 animate-spin text-slate-500" />
                <h2 id="docs-leave-title" className="text-base font-semibold">
                  Zapisywanie przed wyjściem
                </h2>
                <p className="text-sm text-slate-600">
                  Autozapis jeszcze nie zdążył wysłać ostatnich zmian. Poczekaj chwilę…
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-1">
                  <h2 id="docs-leave-title" className="text-base font-semibold">
                    Niezapisane zmiany
                  </h2>
                  <p className="text-sm text-slate-600">{leaveGate.reason}</p>
                </div>
                <div className="flex flex-wrap justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setLeaveGate(null)}>
                    Zostań
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="text-red-700"
                    onClick={() => proceedLeave(leaveGate.target)}
                  >
                    Wyjdź bez zapisu
                  </Button>
                  <Button
                    type="button"
                    disabled={!serverReachable}
                    onClick={() => void attemptLeave(leaveGate.target)}
                  >
                    Spróbuj zapisać
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
